import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Send, Bot, User, Loader2, CheckCircle2, Wrench, SprayCan, Snowflake, Key } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InitialMessage = () => (
  <div className="space-y-3">
    <p className="text-sm md:text-base font-medium">Hei! Jeg er din vaktmester-assistent. 👋</p>
    <p className="text-xs md:text-sm text-slate-600">Vi kan hjelpe deg med:</p>
    <div className="grid grid-cols-2 gap-2">
      <div className="flex items-center gap-2 bg-blue-50 p-2 rounded-lg border border-blue-100">
        <div className="w-7 h-7 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
          <Wrench className="h-3.5 w-3.5 text-white" />
        </div>
        <span className="text-xs font-medium text-slate-700">Reparasjoner</span>
      </div>
      <div className="flex items-center gap-2 bg-cyan-50 p-2 rounded-lg border border-cyan-100">
        <div className="w-7 h-7 bg-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
          <SprayCan className="h-3.5 w-3.5 text-white" />
        </div>
        <span className="text-xs font-medium text-slate-700">Høytrykksvask</span>
      </div>
      <div className="flex items-center gap-2 bg-blue-50 p-2 rounded-lg border border-blue-100">
        <div className="w-7 h-7 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
          <Snowflake className="h-3.5 w-3.5 text-white" />
        </div>
        <span className="text-xs font-medium text-slate-700">Saltstrøing</span>
      </div>
      <div className="flex items-center gap-2 bg-cyan-50 p-2 rounded-lg border border-cyan-100">
        <div className="w-7 h-7 bg-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
          <Key className="h-3.5 w-3.5 text-white" />
        </div>
        <span className="text-xs font-medium text-slate-700">Låsetjenester</span>
      </div>
    </div>
    <p className="text-xs md:text-sm font-medium text-slate-700 pt-2">Hva ønsker du hjelp med?</p>
  </div>
);

const ChatBooking = () => {
  const { toast } = useToast();
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random()}`);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showInitial, setShowInitial] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    // Scroll only the chat container, not the entire page
    if (messagesEndRef.current && chatContainerRef.current) {
      const container = chatContainerRef.current;
      const scrollHeight = container.scrollHeight;
      const height = container.clientHeight;
      const maxScrollTop = scrollHeight - height;
      
      // Smooth scroll to bottom
      container.scrollTo({
        top: maxScrollTop,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    // Only scroll when messages change
    if (messages.length > 0) {
      // Small delay to ensure content is rendered
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [messages.length]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    if (showInitial) {
      setShowInitial(false);
    }

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentMessage = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    // Scroll chat section back into view on mobile
    const scrollChatIntoView = () => {
      const chatSection = document.getElementById('chat-booking');
      if (chatSection && window.innerWidth < 768) {
        chatSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    // Immediate scroll after sending
    setTimeout(() => scrollChatIntoView(), 100);

    try {
      const response = await axios.post(`${API}/chat`, {
        sessionId: sessionId,
        message: currentMessage
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Scroll again when response arrives
      setTimeout(() => scrollChatIntoView(), 200);

      if (response.data.isComplete) {
        setIsComplete(true);
        
        if (response.data.bookingDetails) {
          const bookingData = {
            bookingId: `chat-${sessionId}`,
            ...response.data.bookingDetails,
            status: 'pending'
          };

          await axios.post(`${API}/chat/confirm`, bookingData);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Beklager, det oppsto en feil. Kan du prøve igjen?',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      // Keep focus on input and ensure chat is visible
      setTimeout(() => {
        inputRef.current?.focus();
        scrollChatIntoView();
      }, 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <section id="chat-booking" className="py-12 md:py-20 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 md:w-96 md:h-96 bg-blue-400/5 rounded-full blur-3xl"></div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-6 md:mb-10">
          <span className="text-blue-600 font-bold text-xs md:text-sm uppercase tracking-wider">Enkel booking</span>
          <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 mt-3 mb-2 md:mb-3 px-4">
            Hva ønsker du hjelp med?
          </h2>
          <p className="text-sm md:text-lg text-slate-600 max-w-2xl mx-auto px-4">
            Chat med vår AI-assistent – få prisestimat på få minutter
          </p>
        </div>

        <Card className="shadow-2xl border-2 border-slate-200 overflow-hidden">
          {/* Chat messages */}
          <div 
            ref={chatContainerRef}
            className="h-[420px] md:h-[480px] overflow-y-auto p-3 md:p-5 space-y-3 md:space-y-4 bg-slate-50 scroll-smooth"
          >
            {/* Initial welcome message with services */}
            {showInitial && (
              <div className="flex items-start gap-2 md:gap-3 animate-fade-in">
                <div className="flex-shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center bg-gradient-to-br from-cyan-500 to-blue-500 shadow-md">
                  <Bot className="h-4 w-4 md:h-5 md:w-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="inline-block p-3 md:p-4 rounded-2xl max-w-[95%] md:max-w-[90%] bg-white text-slate-900 shadow-md border-2 border-slate-200">
                    <InitialMessage />
                  </div>
                </div>
              </div>
            )}

            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex items-start gap-2 md:gap-3 ${
                  message.role === 'user' ? 'flex-row-reverse' : ''
                }`}
              >
                <div
                  className={`flex-shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center shadow-md ${
                    message.role === 'user'
                      ? 'bg-blue-600'
                      : 'bg-gradient-to-br from-cyan-500 to-blue-500'
                  }`}
                >
                  {message.role === 'user' ? (
                    <User className="h-4 w-4 md:h-5 md:w-5 text-white" />
                  ) : (
                    <Bot className="h-4 w-4 md:h-5 md:w-5 text-white" />
                  )}
                </div>
                <div
                  className={`flex-1 ${
                    message.role === 'user' ? 'text-right' : 'text-left'
                  }`}
                >
                  <div
                    className={`inline-block p-3 md:p-3.5 rounded-2xl max-w-[85%] md:max-w-[75%] ${
                      message.role === 'user'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-white text-slate-900 shadow-sm border-2 border-slate-200'
                    }`}
                  >
                    <p className="text-sm md:text-base leading-relaxed whitespace-pre-wrap break-words">
                      {message.content}
                    </p>
                  </div>
                  <p className="text-xs text-slate-400 mt-1 px-1">
                    {message.timestamp.toLocaleTimeString('nb-NO', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex items-start gap-2 md:gap-3">
                <div className="flex-shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center bg-gradient-to-br from-cyan-500 to-blue-500 shadow-md">
                  <Bot className="h-4 w-4 md:h-5 md:w-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="inline-block p-3 md:p-3.5 rounded-2xl bg-white shadow-sm border-2 border-slate-200">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-sm text-slate-600">Skriver</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isComplete && (
              <div className="flex justify-center py-3">
                <div className="bg-green-100 border-2 border-green-400 rounded-xl px-4 md:px-5 py-2.5 flex items-center gap-2 shadow-md">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-sm font-semibold text-green-800">
                    Forespørselen er sendt! Vi kontakter deg snart.
                  </span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="border-t-2 border-slate-200 p-3 bg-white">
            <div className="flex items-end gap-2">
              <Input
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={isComplete ? 'Samtalen er fullført' : 'Skriv din melding...'}
                disabled={isLoading || isComplete}
                className="flex-1 h-12 text-base border-2 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl"
                style={{ fontSize: '16px' }}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading || isComplete}
                className="h-12 w-12 p-0 bg-blue-600 hover:bg-blue-700 transition-all flex-shrink-0 shadow-md rounded-xl"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2 text-center hidden md:block">
              Trykk Enter for å sende • Shift+Enter for ny linje
            </p>
          </div>
        </Card>

        <div className="mt-5 md:mt-6 text-center px-4">
          <p className="text-xs md:text-sm text-slate-600">
            <strong>Tips:</strong> Jo mer detaljer, desto bedre estimat
          </p>
        </div>
      </div>
    </section>
  );
};

export default ChatBooking;
